/**
 * A legenda das cores e das etiquetas da grade do mês.
 *
 * Papel: desenha, abaixo da `GradeDoMes`, uma amostra de cada tom de célula —
 * família, todos, família com todos, folga geral, feriado marcado à mão —, o
 * número vermelho do feriado de calendário, as duas etiquetas de pessoa e os
 * selos de previsão e de a definir, cada um com o que significa.
 *
 * Como usar: as amostras repetem as cores de `TOM_DA_CELULA` e das etiquetas da
 * grade — as lavadas um pouco mais fortes (12% em vez de 10%) para se lerem num
 * quadrado de 13 px, e as de família no mesmo azul opaco da célula. Uma cor que mudar na grade deverá mudar aqui também, ou a legenda
 * passará a descrever uma célula que não existe.
 */
import { cn } from '@/shared/lib/utils';

/** As amostras de tom de célula, na ordem em que a legenda as lê. */
const AMOSTRAS = [
  {
    tom: 'border-folha-familia-borda bg-folha-familia',
    significado: 'Família na residência',
  },
  { tom: 'border-sucesso bg-sucesso/12', significado: 'Todos na residência' },
  {
    tom: 'border-folha-familia-borda border-l-4 border-l-sucesso bg-folha-familia',
    significado: 'Família + Todos',
  },
  { tom: 'border-aviso bg-aviso/12', significado: 'Folga TODOS' },
  { tom: 'border-perigo bg-perigo/12', significado: 'Feriado (manual)' },
] as const;

/** Cada item da legenda: a amostra à esquerda e o significado em mono. */
const ITEM = 'flex items-center gap-[5px] font-mono text-[10px] text-texto-fraco';

/** O quadrado de 13 px de toda amostra. */
const QUADRADO = 'inline-block size-[13px] rounded-[2px] border';

/**
 * A legenda da grade.
 *
 * @returns Os itens da legenda, que quebram de linha quando não cabem numa só.
 */
export function LegendaDaGrade() {
  return (
    <div className="mt-3.5 flex flex-wrap gap-2.5">
      {AMOSTRAS.map((amostra) => (
        <div key={amostra.significado} className={ITEM}>
          <span aria-hidden="true" className={cn(QUADRADO, amostra.tom)} />
          {amostra.significado}
        </div>
      ))}
      <div className={ITEM}>
        <span
          aria-hidden="true"
          className={cn(
            QUADRADO,
            'border-linha-forte text-center font-mono text-[8px] leading-[13px] font-bold text-perigo',
          )}
        >
          1
        </span>
        Feriado nacional
      </div>
      <div className={ITEM}>
        <span className="inline-block rounded-[2px] bg-folha-etiqueta-plantao px-1.5 py-0.5 font-sans text-[9.5px] font-medium text-folha-familia-selo">
          Res
        </span>
        Plantão
      </div>
      <div className={ITEM}>
        <span className="inline-block rounded-[2px] bg-aviso/18 px-1.5 py-0.5 font-sans text-[9.5px] font-medium text-aviso">
          Folga
        </span>
        Folga
      </div>
      <div className={ITEM}>
        <span
          aria-hidden="true"
          className={cn(
            QUADRADO,
            'relative border-linha-forte text-center font-mono text-[7px] leading-[13px] font-bold text-perigo',
          )}
        >
          P
        </span>
        Previsão (não confirmado)
      </div>
      <div className={ITEM}>
        <span aria-hidden="true" className={cn(QUADRADO, 'relative border-linha-forte')}>
          <span className="absolute right-px bottom-px font-mono text-[5px] leading-none font-bold text-perigo">
            AD
          </span>
        </span>
        A definir
      </div>
    </div>
  );
}
